<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <form class="relative sm:w-[265px]">
          <label
            class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-black dark:text-white mt-px top-1/2 -translate-y-1/2"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </label>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="directT('ecommerce.orders.searchOrder')"
            class="bg-gray-50 border border-gray-50 h-[36px] text-xs rounded-md w-full block text-black pt-[11px] pb-[12px] ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[13px] ltr:md:pr-[16px] rtl:pl-[13px] rtl:md:pl-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
          />
        </form>
      </div>
    </div>
    <div class="trezo-card-content">
      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
      <div v-else-if="error" class="text-center text-danger-500 py-6">
        {{ error }}
      </div>
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.orderId') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.customer') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.items') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.created') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.total') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orders.vendor') }}
              </th>
              <th
                class="font-medium text-left px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tr-md"
              >
                {{ directT('ecommerce.orders.status') }}
              </th>
              <th
                class="font-medium text-left px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tr-md"
              >
                {{ directT('ecommerce.orders.action') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="order in filteredOrders" :key="order.id">
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ order.orderid }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center">
                  <div class="rounded-md w-[40px]">
                    <img
                      :src="order.customerImage"
                      class="inline-block rounded-md"
                      alt="customer-image"
                      @error="(e) => handleImageError(e, order)"
                    />
                  </div>
                  <div class="ltr:ml-[12px] rtl:mr-[12px]">
                    <span class="block font-medium"> {{ order.name || 'Unknown Customer' }} </span>
                  </div>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ getProductCount(order) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ formatDate(order.created_at) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ formatPrice(order.total) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ order.vendor || 'N/A' }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <span
                  :class="getStatusClass(order.status)"
                >
                  {{ getStatusText(order.status) }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center gap-[9px]">
                  <RouterLink
                    :to="`/ecommerce/order-details/${order.id}`"
                    class="text-primary-500 leading-none custom-tooltip"
                    v-tooltip="directT('ecommerce.orders.view')"
                  >
                    <i class="material-symbols-outlined !text-md">
                      visibility
                    </i>
                  </RouterLink>
                  <RouterLink
                    :to="`/ecommerce/edit-order/${order.id}`"
                    class="text-gray-500 dark:text-gray-400 leading-none custom-tooltip"
                    v-tooltip="directT('ecommerce.orders.edit')"
                  >
                    <i class="material-symbols-outlined !text-md"> edit </i>
                  </RouterLink>
                  <button
                    type="button"
                    class="text-danger-500 leading-none custom-tooltip"
                    v-tooltip="directT('ecommerce.orders.delete')"
                    @click="confirmDelete(order)"
                  >
                    <i class="material-symbols-outlined !text-md"> delete </i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="filteredOrders.length === 0">
              <td colspan="8" class="text-center py-10">
                <div class="flex flex-col items-center justify-center">
                  <i class="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4">shopping_cart</i>
                  <p class="text-lg mb-2">{{ directT('ecommerce.orders.noOrdersYet') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('ecommerce.orders.startByCreating') }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
      </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div class="bg-white dark:bg-[#0c1427] p-6 rounded-md max-w-md w-full">
      <h3 class="text-lg font-medium mb-4">{{ directT('ecommerce.orders.confirmDelete') }}</h3>
      <p class="mb-6">{{ directT('ecommerce.orders.deleteWarning') }}</p>
      <div class="flex justify-end gap-3">
        <button 
          @click="showDeleteModal = false" 
          class="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md"
        >
          {{ directT('ecommerce.orders.cancel') }}
        </button>
        <button 
          @click="deleteSelectedOrder" 
          class="px-4 py-2 bg-danger-500 text-white rounded-md"
        >
          {{ directT('ecommerce.orders.delete') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { getOrders, deleteOrder, Order } from "@/services/orderService";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "OrdersList",
  setup() {
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // State
    const orders = ref<Order[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const searchQuery = ref('');
    const showDeleteModal = ref(false);
    const orderToDelete = ref<Order | null>(null);
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Filtered orders based on search query
    const filteredOrders = computed(() => {
      if (!searchQuery.value) return orders.value;
      
      const query = searchQuery.value.toLowerCase();
      return orders.value.filter(order => 
        order.orderid?.toLowerCase().includes(query) ||
        order.name?.toLowerCase().includes(query) ||
        order.email?.toLowerCase().includes(query) ||
        order.vendor?.toLowerCase().includes(query) ||
        order.status?.toLowerCase().includes(query)
      );
    });
    
    // Format price with currency symbol
    const formatPrice = (price: number | string | undefined): string => {
      if (price === undefined) return '$0.00';
      
      if (typeof price === 'string') {
        // If it's already a formatted string, return it
        if (price.startsWith('$')) return price;
        
        // Try to convert to number
        price = parseFloat(price);
        if (isNaN(price)) return '$0.00';
      }
      
      return locale.value === 'he' || locale.value === 'ar'
        ? `₪${price.toFixed(2)}`
        : `$${price.toFixed(2)}`;
    };
    
    // Format date
    const formatDate = (dateString: string | undefined): string => {
      if (!dateString) return 'N/A';
      
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale.value === 'he' ? 'he-IL' : locale.value === 'ar' ? 'ar-SA' : 'en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    };
    
    // Get product count
    const getProductCount = (order: Order): number => {
      if (!order.products) return 0;
      
      if (Array.isArray(order.products)) {
        return order.products.reduce((total, product) => total + (product.quantity || 1), 0);
      }
      
      return 0;
    };
    
    // Get status display text
    const getStatusText = (status: string | undefined): string => {
      if (!status) return directT('ecommerce.orders.unknown');
      
      const statusKey = `ecommerce.orders.${status.toLowerCase()}`;
      return directT(statusKey);
    };
    
    // Get CSS class for status
    const getStatusClass = (status: string | undefined): string => {
      let baseClass = "px-[8px] py-[3px] inline-block dark:bg-[#15203c] rounded-sm font-medium text-xs";
      
      if (!status) return `${baseClass} bg-gray-100 text-gray-600`;
      
      switch (status.toLowerCase()) {
        case 'processing':
          return `${baseClass} bg-warning-100 text-warning-500`;
        case 'shipped':
        case 'delivered':
          return `${baseClass} bg-success-100 text-success-600`;
        case 'cancelled':
          return `${baseClass} bg-danger-100 text-danger-500`;
        case 'pending':
          return `${baseClass} bg-info-100 text-info-500`;
        default:
          return `${baseClass} bg-gray-100 text-gray-600`;
      }
    };
    
    // Handle image error
    const handleImageError = (e: Event, order: Order) => {
      const target = e.target as HTMLImageElement;
      // Use random user image 1-5
      const userNumber = Math.floor(Math.random() * 5) + 1;
      
      // Create a dynamic import for the image
      import(`@/assets/images/users/user${userNumber}.jpg`).then(module => {
        target.src = module.default;
      }).catch(() => {
        // Fallback to a basic URL if import fails
        target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ccc'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'%3E%3C/path%3E%3C/svg%3E`;
      });
    };
    
    // Confirm delete
    const confirmDelete = (order: Order) => {
      orderToDelete.value = order;
      showDeleteModal.value = true;
    };
    
    // Delete selected order
    const deleteSelectedOrder = async () => {
      if (!orderToDelete.value) return;
      
      const { success, error: deleteError } = await deleteOrder(orderToDelete.value.id);
      
      if (success) {
        orders.value = orders.value.filter(o => o.id !== orderToDelete.value?.id);
        showDeleteModal.value = false;
        orderToDelete.value = null;
      } else {
        error.value = deleteError || 'Failed to delete order';
        setTimeout(() => {
          error.value = null;
        }, 3000);
      }
    };
    
    // Fetch orders
    const fetchOrders = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const { orders: fetchedOrders, error: fetchError } = await getOrders();
        
        if (fetchError) {
          error.value = fetchError;
        } else {
          orders.value = fetchedOrders;
        }
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Failed to fetch orders';
      } finally {
        loading.value = false;
      }
    };
    
    // Lifecycle hooks
    onMounted(() => {
      fetchOrders();
    });

    return {
      orders,
      loading,
      error,
      searchQuery,
      filteredOrders,
      showDeleteModal,
      orderToDelete,
      directT,
      formatPrice,
      formatDate,
      getProductCount,
      getStatusText,
      getStatusClass,
      handleImageError,
      confirmDelete,
      deleteSelectedOrder
    };
  }
});
</script>
